import { useCallback, useContext, useEffect } from 'react';
import { useDashboardContext } from '../../../contexts/DashboardContext';
import { UserContext } from '../../../contexts/UserContext';
import {
  useLandingPageAgentSession,
  UseLandingPageAgentSessionReturn,
} from '../../../hooks/useLandingPageAgentSession';
import { usePresence } from '../../../hooks/usePresence';
import {
  useAgentSessionActions,
  UseAgentSessionActionsReturn,
} from './useAgentSessionActions';
import { useEditorActions, UseEditorActionsReturn } from './useEditorActions';
import {
  useEditorAnalytics,
  UseEditorAnalyticsReturn,
} from './useEditorAnalytics';
import { useEditorData, UseEditorDataReturn } from './useEditorData';
import { useEditorModals, UseEditorModalsReturn } from './useEditorModals';
import { useEditorState, UseEditorStateReturn } from './useEditorState';
import {
  useIframeMessaging,
  UseIframeMessagingReturn,
} from './useIframeMessaging';
import {
  useWebsiteUpdater,
  UseWebsiteUpdaterReturn,
} from './useWebsiteUpdater';

export type UseEditorReturn = {
  mode: 'globalSection' | 'website';
  agentChangeClick: (sectionId: string) => void;
  presenceUsers: any[];
  presenceCursors: any;
} & UseLandingPageAgentSessionReturn &
  UseEditorStateReturn &
  UseEditorDataReturn &
  UseWebsiteUpdaterReturn &
  UseEditorActionsReturn &
  UseIframeMessagingReturn &
  UseEditorModalsReturn &
  UseAgentSessionActionsReturn &
  UseEditorAnalyticsReturn;

interface GlobalSectionUseEditorProps {
  mode: 'globalSection';
  globalSectionId: string;
}

interface WebsiteUseEditorProps {
  mode: 'website';
  dynamicPageId: string;
  initialVariantId: string;
  initialWebsiteId: string;
}

type UseEditorProps = (GlobalSectionUseEditorProps | WebsiteUseEditorProps) & {
  onClose: () => void;
  initialShowAnalytics?: boolean;
};

export function useEditorLogic({
  onClose,
  initialShowAnalytics,
  ...props
}: UseEditorProps): UseEditorReturn {
  const { selectedAccount } = useContext(UserContext);

  // Initialize state
  const editorState = useEditorState(
    props.mode === 'website'
      ? {
          mode: 'website',
          dynamicPageId: props.dynamicPageId,
          initialVariantId: props.initialVariantId,
          initialWebsiteId: props.initialWebsiteId,
          initialShowAnalytics,
        }
      : {
          mode: 'globalSection',
          globalSectionId: `globalSectionId_${props.globalSectionId}`,
          initialShowAnalytics,
        },
  );

  // Presence tracking for editor
  const presence = usePresence({
    mode: 'editor',
    accountId: selectedAccount?.id || '',
    websiteId: editorState.selectedWebsiteId || '',
  });

  // Callback to handle cursor updates from iframe
  const handleCursorMove = useCallback(
    (x: number, y: number) => {
      presence.updateCursor(x, y);
    },
    [presence],
  );

  const modals = useEditorModals();
  const { forms, quizzes } = useDashboardContext();

  // Agent session listener for website mode
  const agentSession = useLandingPageAgentSession(
    props.mode === 'website'
      ? {
          websiteId: editorState.selectedWebsiteId ?? '',
          dynamicPageId: props.dynamicPageId,
          setIsEditable: editorState.setIsEditable,
        }
      : null,
  );

  // Initialize data and messaging
  const editorData = useEditorData(
    props.mode === 'website'
      ? {
          mode: 'website',
          dynamicPageId: props.dynamicPageId,
          dynamicPage: editorState.dynamicPage,
          websiteId: editorState.selectedWebsiteId ?? '',
          ready: editorState.ready,
          id: editorState.id,
          setWebsite: editorState.setWebsite,
          setUnpublishedChanges: editorState.setUnpublishedChanges,
          setDynamicPageVariants: editorState.setDynamicPageVariants,
          agentSessionData: agentSession.agentSessionData,
        }
      : {
          mode: 'globalSection',
          globalSectionId: props.globalSectionId,
          ready: editorState.ready,
          id: editorState.id,
          setWebsite: editorState.setWebsite,
          setUnpublishedChanges: editorState.setUnpublishedChanges,
        },
  );

  const websiteUpdater = useWebsiteUpdater({
    dynamicPageId: props.mode === 'website' ? props.dynamicPageId : '',
    websiteId: editorState.selectedWebsiteId ?? '',
    website: editorData.rawWebsite,
    sendMessageToIframe: editorData.sendMessageToIframe,
  });

  const editorActions = useEditorActions({
    isEditable: editorState.isEditable,
    previewMode: editorState.previewMode,
    agentHasUnreviewedChanges: agentSession.agentHasUnreviewedChanges,
    setSidebarCollapsed: editorState.setSidebarCollapsed,
    onClose,
    setActiveWebsiteUpdate: editorState.setActiveWebsiteUpdate,
    setAddSectionModalOpen: modals.setAddSectionModalOpen,
    setSelectImageModalOpen: modals.setSelectImageModalOpen,
    setDeleteSectionModalOpen: modals.setDeleteSectionModalOpen,
    setAddSectionItemModalOpen: modals.setAddSectionItemModalOpen,
    setSectionItemEditModalOpen: modals.setSectionItemEditModalOpen,
    setSidebarOpen: editorState.setSidebarOpen,
    setEditFormModalOpen: modals.setEditFormModalOpen,
    setEditHtmlStepperModalOpen: modals.setEditHtmlStepperModalOpen,
    setReplaceFormModalOpen: modals.setReplaceFormModalOpen,
    setConvertGlobalToLocalModalOpen: modals.setConvertGlobalToLocalModalOpen,
    setConvertLocalToGlobalModalOpen: modals.setConvertLocalToGlobalModalOpen,
    setEditQuillSearchParamMentionModalOpen:
      modals.setEditQuillSearchParamMentionModalOpen,
    setEditCTAButtonModalOpen: modals.setEditCTAButtonModalOpen,
    updateWebsiteByEvent: websiteUpdater.updateWebsiteByEvent,
    website: editorState.website,
    forms,
    quizzes,
    isVisualEditMode: editorState.isVisualEditMode,
    setIsVisualEditMode: editorState.setIsVisualEditMode,
    sendMessageToIframe: editorData.sendMessageToIframe,
    accountId: selectedAccount?.id,
    dynamicPageId: props.mode === 'website' ? props.dynamicPageId : undefined,
    variantId: editorState.selectedVariantId,
    websiteId: editorState.selectedWebsiteId,
    dynamicPage: editorState.dynamicPage,
    variant: editorState.selectedVariant?.data(),
  });

  const iframeMessaging = useIframeMessaging({
    id: editorState.id,
    dynamicPageId:
      props.mode === 'website' ? props.dynamicPageId : props.globalSectionId,
    website: editorState.website,
    isEditable: editorState.isEditable,
    previewMode: editorState.previewMode,
    ready: editorState.ready,
    setReady: editorState.setReady,
    setAddFavoriteSectionModalOpen: modals.setAddFavoriteSectionModalOpen,
    setRemoveFavoriteSectionModalOpen: modals.setRemoveFavoriteSectionModalOpen,
    setAddSectionToCatalogModalOpen: modals.setAddSectionToCatalogModalOpen,
    setRemoveSectionFromCatalogModalOpen:
      modals.setRemoveSectionFromCatalogModalOpen,
    setEditGlobalSectionModalOpen: modals.setEditGlobalSectionModalOpen,
    setEditHtmlActionModalOpen: modals.setEditHtmlActionModalOpen,
    createWebsiteUpdate: websiteUpdater.createWebsiteUpdate,
    handleWebsiteUpdateEvent: editorActions.handleWebsiteUpdateEvent,
    handleGetDataEvent: editorData.handleGetDataEvent,
    handleGenerateSuggestionsEvent: editorData.handleGenerateSuggestionsEvent,
    websiteId: editorState.selectedWebsiteId ?? '',
    device: editorState.device,
    sendMessageToIframe: editorData.sendMessageToIframe,
    isVisualEditMode: editorState.isVisualEditMode,
    setIsVisualEditMode: editorState.setIsVisualEditMode,
    onCursorMove: handleCursorMove,
  });

  // Editor analytics hook
  const editorAnalytics = useEditorAnalytics({
    id: editorState.id,
    dynamicPageId: props.mode === 'website' ? props.dynamicPageId : undefined,
    websiteId: editorState.selectedWebsiteId ?? '',
    showAnalytics: editorState.showAnalytics,
    previewMode: editorState.previewMode,
    sendMessageToIframe: editorData.sendMessageToIframe,
  });

  // Agent session actions hook
  const agentSessionActions = useAgentSessionActions({
    mode: props.mode,
    dynamicPageId: props.mode === 'website' ? props.dynamicPageId : '',
    agentSession,
    websiteUpdater,
    selectedWebsiteId: editorState.selectedWebsiteId ?? null,
    website: editorState.website,
    rawWebsite: editorData.rawWebsite,
    sendMessageToIframe: editorData.sendMessageToIframe,
    moveFromDraft: editorActions.moveFromDraft,
  });

  // Send cursor updates to iframe
  const sendMessageToIframe = editorData.sendMessageToIframe;
  useEffect(() => {
    const cursorsArray = Object.values(presence.cursors);
    sendMessageToIframe({
      eventName: 'displayCursors',
      cursors: cursorsArray,
    });
  }, [presence.cursors, sendMessageToIframe]);

  return {
    mode: props.mode,

    // Editor state
    ...editorState,

    // Modals
    ...modals,

    // Data
    ...editorData,

    // Website updater
    ...websiteUpdater,

    // Actions
    ...editorActions,

    // Iframe messaging
    ...iframeMessaging,

    // Agent session data
    ...agentSession,

    // Agent session actions
    ...agentSessionActions,

    // Analytics
    ...editorAnalytics,

    // Presence
    presenceUsers: presence.users,
    presenceCursors: presence.cursors,
  };
}
